import {boundMethod} from "autobind-decorator";
import React from "react";
import {IntlShape, injectIntl} from "react-intl";

import recordBox from "@/images/icons/record-box.svg";

import {EModuleLicenses, ESopLicenses} from "@/components/license/models";
import {EConnection} from "@/components/preferences/models";
import http from "@/services/http";
import license from "@/services/license";
import {ERoles} from "@/services/models";
import networkDevices from "@/services/network-devices";
import preferences from "@/services/preferences";
import session from "@/services/session";
import {EDeviceClasses} from "@toolbox/models";
import cellTypes from "../../services/cell-types";
import {ICentSopModel} from "../models";
import {IQueueDevice, ISopQueueRequest} from "./models";

import {getDeviceConnection} from "@/components/preferences/DeviceConnection";
import ExecuteButton from "@shared/sop/ExecuteButton";
import ExecuteSop from "@shared/sop/ExecuteSop";
import {device2DeviceApi, hasNoCable} from "@toolbox/functions/device-check";
import {getDeviceFeatures} from "@toolbox/functions/license-functions";
import T from "@translate/T";
import {getLimits} from "../wizard/limits";
import {getDurations, getGlobalProfiles} from "./Index";
import QueueModal from "./QueueModal";

interface ICentExecuteProps {
    intl: IntlShape;
    sop: ICentSopModel;
    messages?: React.RefObject<HTMLDivElement>;
    iconOnly?: boolean;
}

interface ICentExecuteState {
    devices: IQueueDevice[];
    showModal: boolean;
}

class CentExecute extends React.PureComponent<
    ICentExecuteProps,
    ICentExecuteState
> {
    public readonly state: ICentExecuteState = {
        devices: [],
        showModal: false,
    };

    private unsubscribe?: () => void;

    private get executeUrl() {
        const {intl, sop} = this.props;
        const base = document.querySelector("base")!;
        const baseUrl = base.href
            .replace("http://", "sepview://")
            .replace("https://", "sepview-secure://");
        const queries = new URLSearchParams({
            language: intl.locale,
            user: session.user ?? "",
            token: session.token ?? "",
        });

        return `${baseUrl}api/centrifuge/${sop.id}?${queries}`;
    }

    public componentDidMount() {
        this.unsubscribe = preferences.subscribe({
            preferencesChanged: (_, changes) => {
                if (changes.includes("deviceConnections")) {
                    this.forceUpdate();
                }
            },
        });
    }

    public componentWillUnmount() {
        this.unsubscribe?.();
    }

    @boundMethod
    public updateDevices(devices: IQueueDevice[]) {
        this.setState({devices});
    }

    @boundMethod
    public openModal() {
        const {device} = this.props.sop;
        const devices: IQueueDevice[] = networkDevices.devices
            .map((x) => ({...x.device, isOnline: x.isOnline}))
            .filter((x) => networkDevices.isGenericSameDevice(device, x));
        const licensedDevices = license.status.devices
            .filter((val) => val.deviceClass === "LUMiReader")
            .filter((x) => networkDevices.isGenericSameDevice(device, x));

        for (const licensedDevice of licensedDevices) {
            let add = true;
            for (const x of devices) {
                if (networkDevices.sameDevice(licensedDevice, x)) {
                    add = false;
                    break;
                }
            }

            if (add) {
                devices.push({
                    ...licensedDevice,
                    deviceClass: EDeviceClasses.Reader,
                    isOnline: false,
                });
            }
        }

        if (device.fabrication !== 0 || devices.length <= 1) {
            this.setState(
                {devices: [{...device, isOnline: true}]},
                this.enqueue,
            );
            return;
        }

        this.setState({devices, showModal: true});
    }

    @boundMethod
    public afterClose() {
        this.setState({showModal: false});
    }

    @boundMethod
    public async enqueue(firstFree?: boolean) {
        const {id, device} = this.props.sop;
        const devices = firstFree ? [device] : this.state.devices;
        const json: ISopQueueRequest = {id, devices};

        try {
            const response = await http.post("/api/centrifuge/sop/queue", {
                json,
            });
            if (!response.ok) {
                throw new http.HTTPError(response);
            }

            return true;
        } catch {
            return false;
        }
    }

    public render() {
        const {sop} = this.props;
        const features = getDeviceFeatures(sop.device);
        const limits = getLimits(sop.device);
        const seconds = getDurations(
            sop.cycles,
            sop.channels.length,
            sop.wavelengths.length,
            sop.device.deviceClass,
        ).total;
        const deviceConnection = getDeviceConnection(sop.device);

        const sopLicensed =
            license.getDeviceLicenses()[
                device2DeviceApi(sop.device.deviceClass)
            ];
        const deviceLicensed = networkDevices.isLicensed(sop.device);
        const filt =
            !features.includes(EModuleLicenses.Filtration) &&
            sop.filtrationModule;
        const tooLong = seconds > limits.maxDuration;
        const tooShort = seconds < limits.minDuration;
        const profiles =
            getGlobalProfiles(sop.cycles) > limits.maxGlobalProfiles;
        const magnets =
            !features.includes(ESopLicenses.Magnet) &&
            sop.channels.some((x) =>
                cellTypes.magneticCellTypeIds.includes(
                    cellTypes.getIdEverytime(x.cellType),
                ),
            );
        const templates =
            !features.includes(ESopLicenses.Template) && !!sop.templates.length;
        const acsv =
            !features.includes(ESopLicenses.ACsvExport) && !!sop.acsv?.on;
        const temp =
            !features.includes(ESopLicenses.TemperatureRamp) &&
            new Set(sop.cycles.map((x) => x.temperature)).size > 1;
        const ssc =
            !features.includes(ESopLicenses.SectorShapedCells) &&
            sop.channels.some((x) =>
                cellTypes.sscIds.includes(cellTypes.getIdEverytime(x.cellType)),
            );

        if (
            !sopLicensed ||
            !deviceLicensed ||
            filt ||
            tooLong ||
            tooShort ||
            profiles ||
            magnets ||
            templates ||
            acsv ||
            temp ||
            ssc
        ) {
            return null;
        }

        // if the user has no Recorder-role, we do not show the queue or execute button:
        if (!session.hasRole(ERoles.Recorder, sop.project)) {
            return null;
        }

        if (deviceConnection) {
            // if we have ethernet, there are bunch of devices in local network, hence we can queue and one will grab the SOP
            switch (deviceConnection.connection) {
                case EConnection.Ethernet:
                    return this.renderQueue();

                case EConnection.Usb:
                    return this.renderExecute();

                default:
                    return (
                        <React.Fragment>
                            {this.renderQueue()}
                            {this.renderExecute()}
                        </React.Fragment>
                    );
            }
        }

        if (hasNoCable(sop.device)) {
            return this.renderQueue();
        }

        return this.renderExecute();
    }

    private renderQueue() {
        const {iconOnly} = this.props;
        const {showModal, devices} = this.state;

        return (
            <React.Fragment>
                <ExecuteButton
                    icon={recordBox}
                    iconOnly={iconOnly}
                    onClick={this.openModal}
                >
                    <T>Queue</T>
                </ExecuteButton>

                {showModal && (
                    <QueueModal
                        selectedDevices={devices}
                        onChange={this.updateDevices}
                        onSubmit={this.enqueue}
                        afterClose={this.afterClose}
                    />
                )}
            </React.Fragment>
        );
    }

    private renderExecute() {
        const {iconOnly, messages} = this.props;

        return (
            <ExecuteSop
                iconOnly={iconOnly}
                messages={messages}
                executeUrl={this.executeUrl}
            />
        );
    }
}

export default injectIntl(CentExecute);
